// Product details functionality
// Uses the globally initialized Firebase instance from React app


// Firestore functions from React app
let doc: any, getDoc: any;
let db: any;

// Store product data globally so we can re-display it when language changes
let currentProductData: any = null;

// Expose currentProductData to window for React components to access
(window as any).currentProductData = null;

// Wait for Firebase to be initialized by React app
function waitForFirebase(callback: () => void, maxAttempts = 50, attempt = 0) {
  const windowAny = window as any;
  if (windowAny.firebaseDb && windowAny.firebaseInitialized && windowAny.firestoreFunctions) {

    // Get Firestore functions from window (exposed by React)
    doc = windowAny.firestoreFunctions.doc;
    getDoc = windowAny.firestoreFunctions.getDoc;
    
    callback();
  } else if (attempt < maxAttempts) {
    if (attempt % 10 === 0) {
    }
    setTimeout(() => waitForFirebase(callback, maxAttempts, attempt + 1), 100);
  } else {
  }
}

// Helper function to get translation
function getTranslation(key: string, fallback: string): string {
  try {
    const windowAny = window as any;
    const currentLanguage = windowAny.LanguageContext?.getCurrentLanguage() || 'pt';
    // Try to get from window translations if available
    if (windowAny.translations && windowAny.translations.PRODUCT_DETAILS && windowAny.translations.PRODUCT_DETAILS[key]) {
      const translation = windowAny.translations.PRODUCT_DETAILS[key];
      return typeof translation === 'object' ? (translation[currentLanguage] || translation.pt || translation.en) : translation;
    }
  } catch (e) {
  }
  return fallback;
}

// Get product ID from URL parameters (supports both path params and query params)
function getProductIdFromURL(): string | null {
  // First try to get from pathname (React Router: /produto/:id)
  const pathname = window.location.pathname;
  const pathMatch = pathname.match(/\/produto\/([^\/]+)/);
  if (pathMatch && pathMatch[1]) {
    return pathMatch[1];
  }
  
  // Fallback to query parameter (legacy: /product-details?id=...)
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('id');
}

// Helper function to get category display name using CategoryEnum
function getCategoryDisplayName(product: any): string {
  const windowAny = window as any;
  if (!product.categoryId || !windowAny.categoryEnum) {
    return product.productCategory || 'N/A';
  }
  
  try {
    // Get current language from language context or default to 'pt'
    const currentLanguage = windowAny.LanguageContext?.getCurrentLanguage() || 'pt';
    return windowAny.categoryEnum.getCategoryName(product.categoryId, currentLanguage);
  } catch (error) {
    return product.productCategory || 'N/A';
  }
}

// Display product details on the page
function displayProductDetails(product: any) {
  const windowAny = window as any;
  const currentLanguage = windowAny.LanguageContext?.getCurrentLanguage() || 'pt';
  
  // Update product title
  const titleElement = document.getElementById('product-title');
  if (titleElement) {
    const productName = currentLanguage === 'pt' 
      ? (product.productNamePt || product.productNameEn || 'Product')
      : (product.productNameEn || product.productNamePt || 'Product');
    const productPrice = product.productPrice ? `€${product.productPrice}` : '';
    titleElement.innerHTML = `${productName} <span>${productPrice}</span>`;
  }
  
  // Update product image
  const imageElement = document.getElementById('main-product-image') as HTMLImageElement;
  if (imageElement && product.productImages && product.productImages.length > 0) {
    imageElement.src = product.productImages[0];
    const productName = currentLanguage === 'pt' 
      ? (product.productNamePt || product.productNameEn || 'Product')
      : (product.productNameEn || product.productNamePt || 'Product');
    imageElement.alt = productName;
  }
  
  // Update product description
  const descriptionElement = document.getElementById('product-description');
  if (descriptionElement) {
    const description = currentLanguage === 'pt'
      ? (product.productDescriptionPt || product.productDescription || getTranslation('noDescriptionAvailable', 'No description available'))
      : (product.productDescription || product.productDescriptionPt || getTranslation('noDescriptionAvailable', 'No description available'));
    descriptionElement.textContent = description;
  }
  
  // Update product code
  const codeElement = document.getElementById('product-code');
  if (codeElement) {
    const refText = getTranslation('ref', 'REF.');
    codeElement.textContent = `${refText} ${product.productCode || 'N/A'}`;
  }
  
  // Update full description
  const fullDescriptionElement = document.getElementById('product-full-description');
  if (fullDescriptionElement) {
    const description = currentLanguage === 'pt'
      ? (product.productDescriptionPt || product.productDescription || getTranslation('noDetailedDescription', 'No detailed description available for this product.'))
      : (product.productDescription || product.productDescriptionPt || getTranslation('noDetailedDescription', 'No detailed description available for this product.'));
    fullDescriptionElement.textContent = description;
  }
  
  // Update additional information
  const brandInfoElement = document.getElementById('product-brand-info');
  if (brandInfoElement) {
    brandInfoElement.textContent = product.productBrand || 'N/A';
  }
  
  const categoryInfoElement = document.getElementById('product-category-info');
  if (categoryInfoElement) {
    categoryInfoElement.textContent = getCategoryDisplayName(product);
  }
  
  const codeInfoElement = document.getElementById('product-code-info');
  if (codeInfoElement) {
    codeInfoElement.textContent = product.productCode || 'N/A';
  }
  
  
  // Setup event listeners for cart and wishlist buttons
  setupEventListeners(product);
}

// Load and display product details
async function loadProductDetails() {
  try {
    const productId = getProductIdFromURL();
    
    if (!productId) {
      const errorMsg = getTranslation('productNotFound', 'Product not found');
      showError(errorMsg);
      return;
    }
    
    
    const windowAny = window as any;
    // Fetch product data from Firebase
    // Use FirestorePathService exposed by React app to get the correct document path
    let productDocRef;
    if (windowAny.FirestorePathService) {
      // Use FirestorePathService to get document reference
      productDocRef = windowAny.FirestorePathService.getDocRef(db, 'products', productId);
    } else {
      // Fallback to direct path
      productDocRef = doc(db, 'products', productId);
    }
    
    const productDoc = await getDoc(productDocRef);
    
    if (!productDoc.exists()) {
      const errorMsg = getTranslation('productNotFound', 'Product not found');
      showError(errorMsg);
      return;
    }
    
    const productData = productDoc.data();
    
    // Store product data globally
    currentProductData = {
      id: productDoc.id,
      ...productData
    };
    
    // Expose to window for React components
    (window as any).currentProductData = currentProductData;
    
    // Display product information
    displayProductDetails(currentProductData);
    
  } catch (error) {
    const errorMsg = getTranslation('productNotFound', 'Failed to load product details');
    showError(errorMsg);
  }
}

// Add to wishlist functionality using localStorage
function addToWishlist(product: any) {
  try {
    // Get existing wishlist from localStorage
    let wishlist = JSON.parse(localStorage.getItem('wishlist') || '[]');
    
    // Check if product already exists in wishlist
    const existingItem = wishlist.find((item: any) => item.productId === product.id);
    
    if (existingItem) {
      showInfoMessage('Product is already in your wishlist!');
      return;
    }
    
    // Add new item to wishlist
    const wishlistItem = {
      productId: product.id,
      productName: product.productNamePt || product.productNameEn || 'Product',
      productPrice: product.productPrice || 0,
      productImage: product.productImages?.[0] || '',
      productBrand: product.productBrand || '',
      addedAt: new Date().toISOString()
    };
    
    wishlist.push(wishlistItem);
    
    // Save updated wishlist to localStorage
    localStorage.setItem('wishlist', JSON.stringify(wishlist));
    
    showSuccessMessage('Product added to wishlist successfully!');
    
    // Update wishlist counter
    updateWishlistCounter();
    
  } catch (error) {
    showErrorMessage('Failed to add product to wishlist');
  }
}

// Update wishlist counter in the UI
function updateWishlistCounter() {
  const wishlist = JSON.parse(localStorage.getItem('wishlist') || '[]');
  const totalItems = wishlist.length;
  
  // Update wishlist counter in navbar
  const wishlistCounter = document.querySelector('.main-menu__wishlist-count');
  if (wishlistCounter) {
    wishlistCounter.textContent = totalItems.toString();
  }
  
  // Dispatch custom event for other components to listen
  window.dispatchEvent(new CustomEvent('wishlistUpdated', { 
    detail: { totalItems, wishlist } 
  }));
}

// Show success message
function showSuccessMessage(message: string) {
  showMessage(message, 'success');
}

// Show error message
function showErrorMessage(message: string) {
  showMessage(message, 'error');
}

// Show info message
function showInfoMessage(message: string) {
  showMessage(message, 'info');
}

// Show message to user
function showMessage(message: string, type: 'success' | 'error' | 'info') {
  // Create or update message element
  let messageElement = document.getElementById('action-message');
  if (!messageElement) {
    messageElement = document.createElement('div');
    messageElement.id = 'action-message';
    messageElement.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 15px 20px;
      border-radius: 5px;
      color: white;
      font-weight: bold;
      z-index: 10000;
      max-width: 300px;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    `;
    document.body.appendChild(messageElement);
  }
  
  // Set message content and style based on type
  messageElement.textContent = message;
  messageElement.style.backgroundColor = type === 'success' ? '#28a745' : 
                                        type === 'error' ? '#dc3545' : '#17a2b8';
  
  // Auto-hide after 3 seconds
  setTimeout(() => {
    if (messageElement && messageElement.parentNode) {
      messageElement.parentNode.removeChild(messageElement);
    }
  }, 3000);
}

// Setup event listeners for wishlist button
function setupEventListeners(product: any) {
  // Add to wishlist button
  const addToWishlistBtn = document.querySelector('.product-details__buttons-1 a');
  if (addToWishlistBtn) {
    addToWishlistBtn.addEventListener('click', (e) => {
      e.preventDefault();
      addToWishlist(product);
    });
  }
}

// Show error message
function showError(message: string) {
  const titleElement = document.getElementById('product-title');
  if (titleElement) {
    titleElement.innerHTML = `<span style="color: #dc3545;">${message}</span>`;
  }
  
  const descriptionElement = document.getElementById('product-description');
  if (descriptionElement) {
    const tryAgainMsg = getTranslation('tryAgainLater', 'Please try again later or go back to the store.');
    descriptionElement.textContent = tryAgainMsg;
  }
}

// Export initialization function
export function initializeProductDetails() {
  
  // First wait for Firebase, then load product details
  waitForFirebase(() => {
    // Get Firebase db from window
    db = (window as any).firebaseDb;
    
    // Load product details
    loadProductDetails();
  });
}

// Expose functions to window for React to call
(window as any).loadProductDetails = function() {
  if (db && (window as any).firebaseInitialized) {
    loadProductDetails();
  } else {
    waitForFirebase(() => {
      db = (window as any).firebaseDb;
      loadProductDetails();
    });
  }
};

(window as any).reloadProductDetails = function() {
  if (currentProductData) {
    // Re-display with current language
    displayProductDetails(currentProductData);
  } else {
    // If no product data stored, reload from Firebase
    (window as any).loadProductDetails();
  }
};

// Also listen for firebaseReady event
window.addEventListener('firebaseReady', (event: any) => {
  db = event.detail.db;
  // If loadProductDetails is available and we have a product ID, load it
  if ((window as any).loadProductDetails && getProductIdFromURL()) {
    (window as any).loadProductDetails();
  }
});

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function() {
    initializeProductDetails();
  });
} else {
  initializeProductDetails();
}

